import api, { getToken } from './api';

const API_URL = process.env.NODE_ENV === 'production' 
  ? process.env.REACT_APP_VERCEL_API_URL 
  : process.env.REACT_APP_LOCAL_API_URL;

/**
 * Upload a document for a contacted precatorio
 * @param {string} state - State code (BA, MA, etc.)
 * @param {string} precatorioId - Precatorio ID
 * @param {string} docType - Document type identifier
 * @param {File} file - File to upload
 * @returns {Promise} Upload response
 */
export const uploadDocument = async (state, precatorioId, docType, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('docType', docType);

    const token = getToken();
    const response = await fetch(
      `${API_URL}/contacted-precatorios/${state}/${precatorioId}/documents/upload`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      }
    );

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Erro ao enviar documento');
    }

    return data;
  } catch (error) {
    console.error('Error uploading document:', error);
    throw error;
  }
};

/**
 * Get presigned URL for document viewing
 * @param {string} state - State code
 * @param {string} precatorioId - Precatorio ID
 * @param {string} docType - Document type
 * @returns {Promise} Document URL and metadata
 */
export const getDocumentUrl = async (state, precatorioId, docType) => {
  try {
    const response = await api.get(
      `/contacted-precatorios/${state}/${precatorioId}/documents/${docType}/url`
    );
    return response;
  } catch (error) {
    console.error('Error getting document URL:', error);
    throw error;
  }
};

/**
 * Download a document
 * @param {string} state - State code
 * @param {string} precatorioId - Precatorio ID
 * @param {string} docType - Document type
 * @param {string} fileName - Original file name
 */
export const downloadDocument = async (state, precatorioId, docType, fileName) => {
  try {
    const token = getToken();
    const response = await fetch(
      `${API_URL}/contacted-precatorios/${state}/${precatorioId}/documents/${docType}/download`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      throw new Error('Erro ao baixar documento');
    }

    // Create blob from response
    const blob = await response.blob();
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || `documento_${docType}`;
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);

  } catch (error) {
    console.error('Error downloading document:', error);
    throw error;
  }
};

/**
 * Delete a document
 * @param {string} state - State code
 * @param {string} precatorioId - Precatorio ID
 * @param {string} docType - Document type
 * @returns {Promise} Delete response
 */
export const deleteDocument = async (state, precatorioId, docType) => {
  try {
    const response = await api.delete(
      `/contacted-precatorios/${state}/${precatorioId}/documents/${docType}`
    );
    return response;
  } catch (error) {
    console.error('Error deleting document:', error);
    throw error;
  }
};

/**
 * Get list of all documents for a precatorio
 * @param {string} state - State code
 * @param {string} precatorioId - Precatorio ID
 * @returns {Promise} Documents list
 */
export const getDocumentsList = async (state, precatorioId) => {
  try {
    const response = await api.get(
      `/contacted-precatorios/${state}/${precatorioId}/documents`
    );
    return response;
  } catch (error) {
    console.error('Error getting documents list:', error);
    throw error;
  }
};

/**
 * Validate file before upload
 * @param {File} file - File to validate
 * @returns {Object} Validation result
 */
export const validateFile = (file) => {
  const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
  const maxSize = 500 * 1024 * 1024; // 500MB

  if (!file) {
    return { valid: false, error: 'Nenhum arquivo selecionado' };
  }

  if (!allowedTypes.includes(file.type)) {
    return { 
      valid: false, 
      error: 'Tipo de arquivo não permitido. Use PDF, JPEG, JPG ou PNG.' 
    };
  }

  if (file.size > maxSize) {
    return {
      valid: false,
      error: `Arquivo muito grande. Tamanho máximo: 5MB. Seu arquivo: ${(file.size / (1024 * 1024)).toFixed(2)}MB`
    };
  }

  return { valid: true };
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
};

/**
 * Get file icon based on file type
 * @param {string} mimeType - File MIME type
 * @returns {string} Icon name or emoji
 */
export const getFileIcon = (mimeType) => {
  if (!mimeType) return '📄';
  
  if (mimeType === 'application/pdf') return '📑';
  if (mimeType.startsWith('image/')) return '🖼️';
  
  return '📄';
};

/**
 * Get risk analysis results for processo_execucao
 * @param {string} state - State code
 * @param {string} precatorioId - Precatorio ID
 * @returns {Promise} Risk analysis data
 */
export const getRiskAnalysis = async (state, precatorioId) => {
  try {
    const response = await api.get(
      `/contacted-precatorios/${state}/${precatorioId}/processo-execucao/analysis`
    );
    return response;
  } catch (error) {
    console.error('Error getting risk analysis:', error);
    throw error;
  }
};

/**
 * Download risk analysis report as PDF
 * @param {string} state - State code
 * @param {string} precatorioId - Precatorio ID
 */
export const downloadRiskAnalysisPDF = async (state, precatorioId) => {
  try {
    const token = getToken();
    const response = await fetch(
      `${API_URL}/contacted-precatorios/${state}/${precatorioId}/processo-execucao/analysis-pdf`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      throw new Error('Erro ao baixar relatório de análise de risco');
    }

    // Create blob from response
    const blob = await response.blob();
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const timestamp = new Date().getTime();
    link.download = `relatorio_risco_${state}_${precatorioId}_${timestamp}.pdf`;
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);

  } catch (error) {
    console.error('Error downloading risk analysis PDF:', error);
    throw error;
  }
};

const documentService = {
  uploadDocument,
  getDocumentUrl,
  downloadDocument,
  deleteDocument,
  getDocumentsList,
  getRiskAnalysis,
  downloadRiskAnalysisPDF,
  validateFile,
  formatFileSize,
  getFileIcon
};

export default documentService;